/**
 * Firebase SSO helpers for Google and Microsoft sign-in.
 *
 * Initialized lazily on first SSO attempt so that apps without Firebase config
 * (firebaseApiKey/firebaseAuthDomain/firebaseProjectId in config.json) are
 * unaffected.  Each sign-in method throws an Error with a human-readable message
 * that LoginPage surfaces to the user.
 */

import { initializeApp, getApp, getApps } from 'firebase/app';
import {
    getAuth,
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup,
    signOut,
    Auth,
    UserCredential,
} from 'firebase/auth';

let _auth: Auth | null = null;
let _config: FirebaseConfig | null = null;
let _configLoadedAt = 0;
const CONFIG_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface FirebaseConfig {
    firebaseApiKey: string;
    firebaseAuthDomain: string;
    firebaseProjectId: string;
    microsoftSSOEnabled?: boolean;
}

async function loadFirebaseConfig(): Promise<FirebaseConfig> {
    if (_config && Date.now() - _configLoadedAt < CONFIG_TTL_MS) return _config;
    const response = await fetch('/config.json');
    if (!response.ok) throw new Error('Failed to load application configuration');
    const cfg: FirebaseConfig = await response.json();
    // Asymmetric caching: a fetch returning valid keys is cached for
    // CONFIG_TTL_MS, but a fetch returning empty keys is NOT — that way a
    // subsequent deploy that finally sets the Firebase keys is picked up on
    // the very next call without waiting for the TTL to expire. The cost is
    // a re-fetch per call while keys are empty, which only happens in the
    // initial-setup window.
    // Only cache configs that have SSO keys set — if the keys are empty (e.g. during
    // initial setup), skip caching so a subsequent deploy of real keys is picked up
    // immediately without waiting for the TTL to expire.
    if (cfg.firebaseApiKey && cfg.firebaseAuthDomain && cfg.firebaseProjectId) {
        // Warn if the Firebase config has changed since _auth was initialised.
        // _auth is a singleton (never re-initialised) so a config change during
        // a long-lived tab session is invisible to the user — they'll keep
        // using the old config until they hard-refresh. A console warning is
        // at least *visible* in devtools, even if users mostly won't see it.
        if (_config && (
            _config.firebaseApiKey !== cfg.firebaseApiKey ||
            _config.firebaseAuthDomain !== cfg.firebaseAuthDomain ||
            _config.firebaseProjectId !== cfg.firebaseProjectId
        )) {
            console.warn(
                '[SSO] Firebase config has changed since this tab loaded; ' +
                'reload the page to pick up the new config. ' +
                '(_auth is a singleton and cannot be re-initialised.)'
            );
        }
        _config = cfg;
        _configLoadedAt = Date.now();
    }
    return cfg;
}

/**
 * Returns true if the current config.json has all required Firebase SSO keys set.
 * Caches the config so the page-load fetch and the sign-in fetch share the same result.
 */
export async function isSSOConfigured(): Promise<boolean> {
    try {
        const cfg = await loadFirebaseConfig();
        return Boolean(cfg.firebaseApiKey && cfg.firebaseAuthDomain && cfg.firebaseProjectId);
    } catch {
        return false;
    }
}

/**
 * Returns true if Microsoft SSO is explicitly enabled in config.json. Default false —
 * the Microsoft button stays hidden until a customer on Azure AD is onboarded and the
 * Microsoft provider is enabled in the Firebase project.
 */
export async function isMicrosoftSSOEnabled(): Promise<boolean> {
    try {
        const cfg = await loadFirebaseConfig();
        return Boolean(cfg.microsoftSSOEnabled);
    } catch {
        return false;
    }
}

// NOTE: _auth is a singleton that is never invalidated once created.  Config values
// (firebaseApiKey, firebaseAuthDomain, firebaseProjectId) are only read on first
// initialization.  If these values change in a deployment, users with long-lived tabs
// must reload the page for the new config to take effect.  This is an accepted
// short-term limitation — SSO config changes should be accompanied by a deployment
// notification to active users.
async function getFirebaseAuth(): Promise<Auth> {
    if (_auth) return _auth;

    const config = await loadFirebaseConfig();

    if (!config.firebaseApiKey || !config.firebaseAuthDomain || !config.firebaseProjectId) {
        throw new Error(
            'SSO is not configured for this environment. ' +
            'Set firebaseApiKey, firebaseAuthDomain, and firebaseProjectId in config.json.'
        );
    }

    // Use a named Firebase app so we never accidentally pick up an app
    // initialised elsewhere with different config (e.g. a future Firestore
    // client on a different project). `getApps()[0]` would pick whichever
    // happens to be at index 0, which is fragile.
    const APP_NAME = 'bark-sso';
    const app = getApps().some(a => a.name === APP_NAME)
        ? getApp(APP_NAME)
        : initializeApp({
            apiKey: config.firebaseApiKey,
            authDomain: config.firebaseAuthDomain,
            projectId: config.firebaseProjectId,
        }, APP_NAME);

    _auth = getAuth(app);
    return _auth;
}

async function extractTokens(credential: UserCredential): Promise<{ idToken: string; refreshToken: string | null }> {
    // Token-only extraction — DOES NOT sign out of Firebase.
    //
    // The signOut step was previously here, but that meant a backend exchange
    // failure (5xx, network drop) would leave the user with valid tokens and
    // no Firebase session, so a retry would always reopen the OAuth popup.
    // Caller responsibility: invoke signOutFromFirebase() AFTER the backend
    // exchange succeeds; on failure, leave the session intact so the retry
    // can use the same credential.
    const idToken = await credential.user.getIdToken();
    const refreshToken = credential.user.refreshToken || null;
    return { idToken, refreshToken };
}

/**
 * Sign out of the Firebase JS SDK's local session.
 *
 * IMPORTANT: this is CLIENT-SIDE-ONLY. It clears the persisted user object
 * but does NOT revoke the ID token or refresh token server-side — the token
 * remains valid until its natural expiry (~1h for the ID token, indefinite
 * for the refresh token until used). Future contributors: do NOT replace
 * this with auth.revokeAccessToken() or similar — the backend exchange
 * call needs the freshly-issued token to be honoured by Firebase Admin SDK
 * on verify_id_token. Server-side revocation is a separate concern tracked
 * in the rollout plan.
 *
 * Intended to be called from LoginPage *after* the backend `/sso/token`
 * exchange succeeds — keeping the Firebase session alive during the
 * exchange means a transient backend failure can retry against the same
 * credential instead of reopening the OAuth popup.
 */
export async function signOutFromFirebase(): Promise<void> {
    if (!_auth) return; // never initialised; nothing to sign out of
    try {
        await signOut(_auth);
    } catch (e) {
        // Non-fatal: the backend session is still valid, but log so we can detect
        // dangling Firebase sessions that could confuse subsequent SSO attempts.
        console.warn('Firebase signOut failed after SSO token extraction', e);
    }
}

/**
 * Trigger Google SSO popup and return Firebase ID + refresh tokens.
 * Throws on misconfiguration or user cancellation.
 */
export async function signInWithGoogle(): Promise<{ idToken: string; refreshToken: string | null }> {
    const auth = await getFirebaseAuth();
    // Sign out any stale Firebase session before starting a new popup flow so the
    // provider picker is always shown and dangling sessions don't auto-sign-in.
    // Only call signOut when there's actually a current user — otherwise we add
    // a no-op async round-trip on the first sign-in of every fresh session.
    if (auth.currentUser) {
        try { await signOut(auth); } catch (e) { console.debug('[SSO] pre-signOut failed (non-fatal):', e); }
    }
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const credential = await signInWithPopup(auth, provider);
    return extractTokens(credential);
}

/**
 * Trigger Microsoft (Azure AD) SSO popup and return Firebase ID + refresh tokens.
 * Throws on misconfiguration or user cancellation.
 */
export async function signInWithMicrosoft(): Promise<{ idToken: string; refreshToken: string | null }> {
    const auth = await getFirebaseAuth();
    // Sign out any stale Firebase session before starting a new popup flow so
    // the account picker is always shown. Guarded by `auth.currentUser` to
    // match the Google path — no point in an async no-op on the first
    // sign-in of a fresh session.
    if (auth.currentUser) {
        try { await signOut(auth); } catch (e) { console.debug('[SSO] pre-signOut failed (non-fatal):', e); }
    }
    const provider = new OAuthProvider('microsoft.com');
    const credential = await signInWithPopup(auth, provider);
    return extractTokens(credential);
}
